import { createContext, useContext } from 'react';
import type { ShipToAddress } from '@mypoz/shared';

export interface WarehousesState {
  /**
   * Every warehouse a customer may forward to, vendor order preserved. Empty while loading and
   * after a failure — callers branch on `status`, not on the length of this.
   */
  readonly warehouses: readonly ShipToAddress[];
  readonly status: 'loading' | 'ready' | 'error';
  /** Asks the vendor again. Safe to call at any time; a fetch already in flight is discarded. */
  reload: () => void;
}

export const WarehousesContext = createContext<WarehousesState | null>(null);

export function useWarehouses(): WarehousesState {
  const value = useContext(WarehousesContext);
  if (!value) throw new Error('useWarehouses must be used inside WarehousesProvider');
  return value;
}

/**
 * One warehouse by id, or undefined.
 *
 * Undefined is a real answer, not only a loading artefact: a package can name a warehouse the
 * vendor has since hidden from this list, and the two sea-freight loading warehouses are exactly
 * that case. Callers fall back to showing the raw id rather than hiding the row.
 */
export function useWarehouse(warehouseId: string | undefined): ShipToAddress | undefined {
  const { warehouses } = useWarehouses();
  return warehouses.find((warehouse) => warehouse.id === warehouseId);
}
